import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Section } from '@/components/Section';
import {
  getSchedule,
  type EquipmentRequirements,
  type ScheduleDay,
  type ScheduleSlot,
  type TrainingPlan,
} from '@/lib/api';

/**
 * Maps a purchased membership program (or trial class) to the schedule
 * categories a member of that program actually attends. Used to narrow the
 * weekly grid to "just the classes you purchased" when the page is reached
 * from a signup confirmation (`/schedule?programs=Boxing,MMA`).
 */
const PROGRAM_CATEGORIES: Record<string, ScheduleSlot['category'][]> = {
  Boxing: ['boxing', 'mixed'],
  MuayThai: ['muay-thai', 'mixed'],
  MMA: ['mma', 'wrestling'],
  BJJ: ['bjj'],
  Kids: ['kids'],
};

/**
 * Program token → `TrainingPlan.discipline`. A member's real answer to "which
 * classes do I attend" is their skill-level training plan, not the raw weekly
 * grid, so a filtered view leads with the plans for these disciplines.
 * Kids has no levelled plan — those members get the grid.
 */
const PROGRAM_DISCIPLINE: Record<string, string> = {
  Boxing: 'Boxing',
  MuayThai: 'Muay Thai',
  MMA: 'MMA',
  BJJ: 'Brazilian Jiu Jitsu',
};

const PROGRAM_LABEL: Record<string, string> = {
  Boxing: 'Boxing',
  MuayThai: 'Muay Thai',
  MMA: 'MMA',
  BJJ: 'Brazilian Jiu-Jitsu',
  Kids: 'Kids',
};

const DAY_ORDER: ScheduleDay[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
];

const DAY_LABEL: Record<ScheduleDay, string> = {
  MONDAY: 'Monday',
  TUESDAY: 'Tuesday',
  WEDNESDAY: 'Wednesday',
  THURSDAY: 'Thursday',
  FRIDAY: 'Friday',
  SATURDAY: 'Saturday',
};

const CATEGORY_TONE: Record<ScheduleSlot['category'], string> = {
  boxing: 'border-l-rose-400/70',
  'muay-thai': 'border-l-amber-400/70',
  bjj: 'border-l-sky-400/70',
  mma: 'border-l-violet-400/70',
  kids: 'border-l-emerald-400/70',
  wrestling: 'border-l-orange-400/70',
  sparring: 'border-l-red-500/80',
  mixed: 'border-l-bone/60',
};

function timeToMinutes(t: string): number {
  const m = t.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!m) return 0;
  let h = Number(m[1]);
  const min = Number(m[2]);
  const ampm = m[3].toUpperCase();
  if (ampm === 'PM' && h !== 12) h += 12;
  if (ampm === 'AM' && h === 12) h = 0;
  return h * 60 + min;
}

export function SchedulePage() {
  const [params] = useSearchParams();
  const [schedule, setSchedule] = useState<ScheduleSlot[]>([]);
  const [plans, setPlans] = useState<TrainingPlan[]>([]);
  const [equipment, setEquipment] = useState<EquipmentRequirements | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getSchedule()
      .then((r) => {
        setSchedule(r.schedule);
        setPlans(r.trainingPlans);
        setEquipment(r.equipment);
      })
      .catch(() => setError('Could not load schedule.'));
  }, []);

  // When the page is opened from a signup confirmation, `?programs=Boxing,MMA`
  // narrows the grid to the categories that program attends. Unknown tokens are
  // ignored; with no valid tokens the full schedule shows (e.g. the nav link).
  const programFilter = useMemo(() => {
    const tokens = (params.get('programs') ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter((t) => t in PROGRAM_CATEGORIES);
    return tokens.length > 0 ? Array.from(new Set(tokens)) : null;
  }, [params]);

  const unlevelledCategories = useMemo(() => {
    if (!programFilter) return null;
    const set = new Set<ScheduleSlot['category']>();
    for (const p of programFilter) {
      // Adult programs use George's approved skill-level plans below. The
      // category grid was the source of the wrong schedules because it showed
      // every same-discipline class regardless of the member's level.
      if (PROGRAM_DISCIPLINE[p] !== undefined) continue;
      for (const c of PROGRAM_CATEGORIES[p]) set.add(c);
    }
    return set;
  }, [programFilter]);

  const visibleSchedule = useMemo(
    () =>
      unlevelledCategories === null
        ? schedule
        : schedule.filter((s) => unlevelledCategories.has(s.category)),
    [schedule, unlevelledCategories]
  );

  const byDay = useMemo(() => {
    const map: Record<ScheduleDay, ScheduleSlot[]> = {
      MONDAY: [],
      TUESDAY: [],
      WEDNESDAY: [],
      THURSDAY: [],
      FRIDAY: [],
      SATURDAY: [],
    };
    for (const s of visibleSchedule) map[s.day].push(s);
    for (const d of DAY_ORDER)
      map[d].sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));
    return map;
  }, [visibleSchedule]);

  const slotById = useMemo(() => {
    const map = new Map<string, ScheduleSlot>();
    for (const slot of schedule) map.set(slot.id, slot);
    return map;
  }, [schedule]);

  // Filtered view shows only the purchased disciplines' plans; unfiltered shows all.
  const visiblePlans = useMemo(() => {
    if (!programFilter) return plans;
    const wanted = new Set(
      programFilter.map((p) => PROGRAM_DISCIPLINE[p]).filter((d): d is string => Boolean(d))
    );
    return plans.filter((p) => wanted.has(p.discipline));
  }, [plans, programFilter]);

  return (
    <Section eyebrow="Weekly" title="Class schedule.">
      {error && <p className="text-accent">{error}</p>}

      {programFilter && (
        <div
          className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-accent/40 bg-accent/10 p-4 text-sm"
          data-testid="program-filter-banner"
        >
          <span className="text-bone/90">
            {programFilter.some((program) => PROGRAM_DISCIPLINE[program] !== undefined)
              ? 'Showing the approved training plan for your membership: '
              : 'Showing classes for your membership: '}
            <strong>{programFilter.map((p) => PROGRAM_LABEL[p] ?? p).join(', ')}</strong>
          </span>
          <Link
            to="/schedule"
            className="font-medium text-accent underline-offset-2 hover:underline"
          >
            View full schedule →
          </Link>
        </div>
      )}

      {programFilter && visiblePlans.length > 0 && (
        <TrainingPlans plans={visiblePlans} slotById={slotById} filtered />
      )}

      {/* ── Weekly grid: stacked Mon → Sat on mobile, columnar on desktop ── */}
      {visibleSchedule.length > 0 && (
        <div
          className={`grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-6 lg:gap-4 ${
            programFilter ? 'mt-8' : ''
          }`}
          data-testid="weekly-schedule"
        >
          {DAY_ORDER.map((d) => {
            const slots = byDay[d];
            return (
              <div key={d} className="flex flex-col gap-3" data-testid={`schedule-day-${d}`}>
                <h3 className="font-display text-xl text-bone">{DAY_LABEL[d]}</h3>
                {slots.length === 0 ? (
                  <p className="text-sm text-bone/40">No classes.</p>
                ) : (
                  slots.map((s) => (
                    <div
                      key={s.id}
                      className={`rounded-md bg-neutral-900/50 p-3 border-l-2 ${CATEGORY_TONE[s.category]}`}
                    >
                      <div className="text-sm font-medium text-bone leading-tight">{s.title}</div>
                      <div className="mt-1 font-mono text-xs text-bone/60">
                        {s.startTime} - {s.endTime}
                      </div>
                      {s.trialEligible && (
                        <div className="mt-1 text-[10px] uppercase tracking-wider text-emerald-400">
                          Trial-friendly
                        </div>
                      )}
                      {s.notes && (
                        <div className="mt-1 text-[10px] uppercase tracking-wider text-amber-400/90">
                          {s.notes}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Equipment ────────────────────────────────────────────────── */}
      {!programFilter && equipment && (
        <div className="mt-12">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-display text-3xl">Equipment Requirements</h3>
            <a
              href={SHOP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-xs"
            >
              Shop school gear →
            </a>
          </div>
          <dl className="mt-4 grid gap-4 sm:grid-cols-2">
            <Definition term="Beginners" def={equipment.beginners} />
            <Definition term="Boxing - Intermediate" def={equipment.boxingIntermediate} />
            <Definition term="Muay Thai - Intermediate" def={equipment.muayThaiIntermediate} />
            <Definition term="School T-shirt" def={equipment.schoolTShirt} />
          </dl>
        </div>
      )}

      {/* ── Training plans by discipline ─────────────────────────────── */}
      {!programFilter && visiblePlans.length > 0 && (
        <TrainingPlans plans={visiblePlans} slotById={slotById} />
      )}
    </Section>
  );
}

const SHOP_URL = 'https://mma-boxing-gym.myshopify.com';

function TrainingPlans({
  plans,
  slotById,
  filtered = false,
}: {
  plans: TrainingPlan[];
  slotById: ReadonlyMap<string, ScheduleSlot>;
  filtered?: boolean;
}) {
  return (
    <div className={filtered ? '' : 'mt-12'} data-testid="training-plans">
      <h3 className="font-display text-3xl">
        {filtered ? 'Your training plan by skill level' : 'Training plans by skill level'}
      </h3>
      <p className="mt-2 max-w-2xl text-sm text-bone/60">
        Your coach decides your skill level and workout times. Use the row for your level; it is the
        exact weekly workout schedule George approved.
      </p>
      <div className="mt-8 space-y-12">
        {plans.map((plan) => (
          <div key={plan.discipline} className="space-y-6">
            <h4 className="font-display text-2xl">{plan.discipline}</h4>
            {plan.levels.map((level) => {
              const workouts =
                level.workouts ??
                (level.slotIds ?? []).flatMap((id) => {
                  const slot = slotById.get(id);
                  return slot
                    ? [{ day: slot.day, startTime: slot.startTime, endTime: slot.endTime }]
                    : [];
                });
              const workoutsForDay: Record<ScheduleDay, (typeof workouts)[number][]> = {
                MONDAY: [],
                TUESDAY: [],
                WEDNESDAY: [],
                THURSDAY: [],
                FRIDAY: [],
                SATURDAY: [],
              };
              for (const workout of workouts) {
                workoutsForDay[workout.day].push(workout);
              }
              for (const day of DAY_ORDER) {
                workoutsForDay[day].sort(
                  (a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
                );
              }
              return (
                <div key={level.title}>
                  <div className="flex items-baseline gap-3">
                    <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">
                      {level.title}
                    </span>
                    {level.notes && <span className="text-xs text-bone/50">{level.notes}</span>}
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                    {DAY_ORDER.map((day) => (
                      <div key={day} className="flex flex-col gap-2">
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-bone/40">
                          {DAY_LABEL[day]}
                        </div>
                        {workoutsForDay[day].length === 0 ? (
                          <div className="min-h-[60px]" aria-hidden="true" />
                        ) : (
                          workoutsForDay[day].map((workout, index) => (
                            <div
                              key={`${workout.startTime}-${workout.endTime}-${index}`}
                              className="rounded-md border-l-2 border-l-accent/70 bg-neutral-900/50 p-2.5"
                            >
                              <div className="font-mono text-xs text-bone/80">
                                {workout.startTime} - {workout.endTime}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function ShopLink({ children }: { children: ReactNode }) {
  return (
    <a
      href={SHOP_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="text-accent underline-offset-2 hover:underline"
    >
      {children}
    </a>
  );
}

/** Linkify "dallasmmaboxing.com" mentions in the equipment text. */
function linkifyEquipment(s: string): ReactNode {
  // Build a list of segments split on the known anchor.
  const parts: ReactNode[] = [];
  const re = /(dallasmmaboxing\.com)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(s)) !== null) {
    if (match.index > lastIndex) parts.push(s.slice(lastIndex, match.index));
    parts.push(<ShopLink key={`s-${match.index}`}>dallasmmaboxing.com</ShopLink>);
    lastIndex = match.index + match[1].length;
  }
  if (lastIndex < s.length) parts.push(s.slice(lastIndex));
  return parts;
}

function Definition({ term, def }: { term: string; def: string }) {
  return (
    <div className="rounded-lg border border-bone/10 bg-neutral-900/40 p-4">
      <dt className="text-xs uppercase tracking-wider text-accent">{term}</dt>
      <dd className="mt-2 text-sm text-bone/80">{linkifyEquipment(def)}</dd>
    </div>
  );
}
